import { useEffect, useState } from "react"
import { IComponentMetaData } from "../../SurveyModel/types/commonTypes"
import { INode } from "../../SurveyModel/types/nodeTypes"
import InputLabel from "@/components/Atoms/InputLabel"
import InputField from "@/components/Atoms/InputField"
import { useSelector } from "react-redux"
import { RootState } from "@/reducers/store"
import { useValidate } from "@/state/survey/formSlice";
import { BasicAnswerSave, useUpdateAnswersSubmit } from "@/state/survey/saveAnswerSlice"
import { useResponseId } from "@/components/hooks"
import { Utility, Validate } from "@/utils"
import { DECIMAL_3_DIGITS_1_DP_REGEX, TWO_DIGIT_REGEX } from "@/constants/constants";

interface IHeightAndWeightInput {
    node: INode
    // children: ReactNode
    name: string
    disabled: boolean
    metaData?: IComponentMetaData
    repeatedElement?: boolean
    onBlur?: () => void
    type?: string
}

const UNIT = {
    CM: "cm",
    FT: "ft"
};

const HEIGHT_KEY = "Height";
const WEIGHT_KEY = "Weight";

const HeightAndWeightInput: React.FC<IHeightAndWeightInput> = ({name, node, onBlur, disabled}: IHeightAndWeightInput) => {
    const [isAlert, setIsAlert] = useState(false);
    const validateInputField = useValidate();
    const form = useSelector((state: RootState) => state.form);
    const [unit, setUnit] = useState(UNIT.CM);
    const {answerResponseId} = useResponseId();
    const updateAnswersDispatch = useUpdateAnswersSubmit();

    const inputsDisabled = disabled || node.disabled;

    // source of truth, in cm
    const { state: stateHeight = {}, value: heightValue = 0 } = form[HEIGHT_KEY] || {};
    // source of truth, in kg
    const { state: stateWeight = {}, value: weightValue = 0 } = form[WEIGHT_KEY] || {};

    const [heightCM, setHeightCM] = useState<number>(heightValue as number || 0);
    const [weightKG, setWeightKG] = useState<number>(weightValue as number || 0);

    useEffect(() => {
        if (node.alerts) {
           node.alerts.forEach((alert) => {
                const error = Utility.evaluateManually(form, alert.expression);
                if (error) {
                    setIsAlert(true);
                } else {
                    setIsAlert(false);
                }
            });
        }
    }, [node, form]);

    useEffect(() => {
        validateInputField(HEIGHT_KEY, heightCM, true);
        validateInputField(WEIGHT_KEY, weightKG, true);
        // just to trigger the section related validations
        // Section.tsx listens on each node. since we are changing height and weight individually
        // alerts are not triggered. so we need to trigger them manually.
        validateInputField(name, `${heightCM}-${weightKG}`, false);
    }, [heightCM, weightKG]);

    const { feet, inches } = Utility.convertHeightToImperial(heightCM);
    const { stone, pounds } = Utility.convertWeightToImperial(weightKG);

    const toggleMetricUnit = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (unit === UNIT.CM) {
            setUnit(UNIT.FT);
        } else {
            setHeightCM(Math.round(heightCM * 10) / 10);
            setWeightKG(Math.round(weightKG * 10) / 10);
            setUnit(UNIT.CM);
        }
    }

    const handleOnBlurHeight = () => {
        const answers: BasicAnswerSave[] = [{
            name: HEIGHT_KEY,
            value: Math.round(heightCM * 10) / 10,
        }];
        if (answerResponseId) {
            updateAnswersDispatch(answers, answerResponseId);
        }
        onBlur && onBlur();
    }

    const handleOnBlurWeight = () => {
        const answers: BasicAnswerSave[] = [{
            name: WEIGHT_KEY,
            value: Math.round(weightKG * 10) / 10,
        }];
        if (answerResponseId) {
            updateAnswersDispatch(answers, answerResponseId);
        }
        onBlur && onBlur();
    }

    const handleOnChangeCM = (value: string) => {
        setHeightCM(Number(value || 0));
    }

    const handleOnChangeKG = (value: string) => {
        setWeightKG(Number(value || 0));
    }

    const handleFtChange = (value: string) => {
        const feet = Number(value || 0);
        const totalInches = (feet * 12) + inches;
        const cm = totalInches * 2.54;
        setHeightCM(cm);
    }

    const handleInchesChange = (value: string) => {
        if (Number(value) >= 12) return;
        const inchesInput = Number(value || 0);
        const totalInches = (feet * 12) + inchesInput;
        const cm = totalInches * 2.54;
        setHeightCM(cm);
    }

    const handleOnStonesChange = (value: string) => {
        const stoneInput = Number(value || 0);
        const totalPounds = (stoneInput * 14) + pounds;
        const kg = totalPounds / 2.20462;
        setWeightKG(kg);
    }

    const handleOnPoundsChange = (value: string) => {
        if (Number(value) >= 14) return;
        const poundsInput = Number(value || 0);
        const totalPounds = (stone * 14) + poundsInput;
        const kg = totalPounds / 2.20462;
        setWeightKG(kg);
    }

    const validateCM = (value: string) => {
        if (!DECIMAL_3_DIGITS_1_DP_REGEX.test(value) && value !== '') return false;
        return true;
    };

    const validateFeet = (value: string) => {
        if (!TWO_DIGIT_REGEX.test(value) && value !== '') return false;
        return true;
    };

    const validateKG = (value: string) => {
        if (!DECIMAL_3_DIGITS_1_DP_REGEX.test(value) && value !== '') return false;
        return true;
    };

    const validateStone = (value: string) => {
        if (!TWO_DIGIT_REGEX.test(value) && value !== '') return false;
        return true;
    };

    const validateInches = (value: string) => {
        if (Number(value) >= 12) return false;
        return true;
    };

    const validatePounds = (value: string) => {
        if (Number(value) >= 14) return false;
        return true;
    };

    return (
        <div className={`height-and-weight-wrap ${isAlert ? 'red-strip' : ''}`}>
            <div className="flex flex-row input-container">
                <div className="flex input-group">
                    {unit === UNIT.CM && <div className="user-input w-[120px]">
                        <InputLabel label={"Centimetres"} labelClasses="input-label" />
                        <InputField disabled={inputsDisabled} validate={validateCM} form={form} decimalPlaces={1} isInValid={Validate.isInvalid(stateHeight)} propClasses={'single-line-text flex'} type={"Number"} value={heightCM?.toString()} name={HEIGHT_KEY} onChange={handleOnChangeCM} onBlur={handleOnBlurHeight} />
                    </div>}
                    {unit === UNIT.FT && <div className="user-input w-[120px]">
                        <InputLabel label={"Feet"} labelClasses="input-label" />
                        <InputField disabled={inputsDisabled} validate={validateFeet} form={form} isInValid={Validate.isInvalid(stateHeight)} propClasses={'single-line-text flex'} type={"Number"} value={feet.toString()} name={HEIGHT_KEY} onChange={handleFtChange} onBlur={handleOnBlurHeight} />
                    </div>}
                    {unit === UNIT.FT && <div className="user-input w-[120px]">
                        <InputLabel label={"Inches"} labelClasses="input-label" />
                        <InputField disabled={inputsDisabled} validate={validateInches} form={form} isInValid={Validate.isInvalid(stateHeight)} propClasses={'single-line-text flex'} type={"Number"} value={Math.round(inches).toString()} name={HEIGHT_KEY} onChange={handleInchesChange} onBlur={handleOnBlurHeight} />
                    </div>}
                </div>
                <div className="divider" />
                <div className="flex input-group">
                    {unit === UNIT.CM && <div className="user-input w-[120px]">
                        <InputLabel label={"Kilograms"} labelClasses="input-label" />
                        <InputField disabled={inputsDisabled} validate={validateKG} form={form} decimalPlaces={1} isInValid={Validate.isInvalid(stateWeight)} propClasses={'single-line-text flex'} type={"Number"} value={weightKG?.toString()} name={WEIGHT_KEY} onChange={handleOnChangeKG} onBlur={handleOnBlurWeight} />
                    </div>}
                    {unit === UNIT.FT && <div className="user-input w-[120px]">
                        <InputLabel label={"Stone"} labelClasses="input-label" />
                        <InputField disabled={inputsDisabled} validate={validateStone} form={form} isInValid={Validate.isInvalid(stateWeight)} propClasses={'single-line-text flex'} type={"Number"} value={Math.round(stone).toString()} name={WEIGHT_KEY} onChange={handleOnStonesChange} onBlur={handleOnBlurWeight} />
                    </div>}
                    {unit === UNIT.FT && <div className="user-input w-[120px]">
                        <InputLabel label={"Pounds"} labelClasses="input-label" />
                        <InputField disabled={inputsDisabled} validate={validatePounds} form={form} isInValid={Validate.isInvalid(stateWeight)} propClasses={'single-line-text flex'} type={"Number"} value={pounds.toFixed(0).toString()} name={WEIGHT_KEY} onChange={handleOnPoundsChange} onBlur={handleOnBlurWeight} />
                    </div>}
                </div>
                <div className="divider" />
                <div className="user-input w-[120px]">
                    <InputLabel label={"BMI"} labelClasses="input-label" />
                    <InputField readOnly disabled={inputsDisabled} form={form} isInValid={false} propClasses={'single-line-text bottom-border-only flex'} propDivClasses="background-none" type={"Number"} value={(weightKG / ((heightCM/100) * (heightCM/100))).toFixed(1)} name={"weight"} onChange={null} />
                </div>
            </div>
            <a href="#" className="switch-unit-button" onClick={toggleMetricUnit}>
                {unit === UNIT.CM ? "Switch to imperial" : "Switch to metric"}
            </a>
        </div>
    );
}

export default HeightAndWeightInput;
